<template>
    <listings-find
        :params="{
            listing: $route.params.listing
        }"
        :include="[
            'applications(paginate:1,5|include:applicants(paginate:1,5|include:tenant))',
            'files(filters:with_erased=false)',
            'note',
            'property'
        ]"
        v-slot="{
            sid,
            has_loaded,
            is_loading,
            data: listing,
            error,
            find
        }"
    >
        <div>
            <template v-if="!has_loaded || error">
                <base-loading
                    :retry="find"
                    :error="error"
                    class="py-6"
                />

                <error-modal
                    v-if="error && $error.is(error, { exclude: [400, 422] })"
                    :error="error"
                />
            </template>

            <template v-else>
                <base-hint name="landlords-listings-show">
                    <template slot="heading">
                        Viewing a listing
                    </template>

                    <p class="mb-3">
                        You're currently viewing your listing for {{ listing.property.name }}.
                    </p>

                    <button
                        class="w-full btn btn-sm btn-grey-darker md:w-auto"
                        @click.prevent="introjs"
                    >
                        What's on this page?
                    </button>
                </base-hint>

                <base-alert
                    v-if="listing.is_removed"
                    level="danger"
                    class="mb-6"
                >
                    <template slot="heading">Listing Removed</template>

                    <template slot="content">
                        <p class="mb-3">
                            Your listing has been removed by Rentora - either automatically, or by a human.
                            There are several reasons that this can happen:
                        </p>

                        <ul class="mb-3 ml-5 list-disc">
                            <li>We believe this listing is a scam or fraudulent</li>
                            <li>
                                It is in breach of our
                                <a
                                    href="https://help.rentora.co.uk/article/45-rules-for-listing"
                                    class="underline"
                                >
                                    listing rules,
                                </a>
                                <laroute-link
                                    :to="{ name: 'www.policies.terms.index' }"
                                    class="underline"
                                >
                                    terms
                                </laroute-link>
                                or
                                <laroute-link
                                    :to="{ name: 'www.policies.privacy.index' }"
                                    class="underline"
                                >
                                    privacy policy
                                </laroute-link>
                            </li>
                        </ul>

                        <p>
                            Listings are removed at our discression. If you don't would like to contest our decision, please
                            <a
                                href="https://help.rentora.co.uk/contact"
                                class="underline"
                            >
                                contact us.
                            </a>
                        </p>
                    </template>
                </base-alert>

                <base-alert
                    v-if="!listing.is_active"
                    level="warning"
                    class="mb-6"
                >
                    <template slot="heading">Draft Listing</template>

                    <template slot="content">
                        <p>This listing is a draft and not yet active (it is not visible to the public). Once your listing is ready, please activate it below.</p>
                    </template>
                </base-alert>

                <details-card class="mb-6 id-details-card">
                    <template slot="header">
                        <div class="flex-grow mr-3 overflow-hidden">
                            <h1 class="text-lg font-normal truncate">
                                Property Listing of {{ listing.property.name }}
                            </h1>
                        </div>

                        <div
                            v-if="is_loading"
                            class="w-6 h-6 mr-3"
                        >
                            <base-svg
                                name="icon-loading"
                                class="w-full h-full"
                            />
                        </div>

                        <edit-dropdown
                            :listing="listing"
                            class="flex-none"
                            @success="find"
                        />
                    </template>

                    <template slot="body">
                        <grid-layout class="-mt-3">
                            <grid-layout-item class="mt-3 sm:w-1/2 lg:w-1/3">
                                <div class="text-xs tracking-wide uppercase text-grey-darker">Property</div>

                                <div class="text-sm">
                                    <router-link
                                        :to="{
                                            name: $router.fix('dashboard.landlords.properties.show'),
                                            params: {
                                                property: listing.property.uuid
                                            }
                                        }"
                                        class="no-underline text-blue"
                                    >
                                        {{ listing.property.name }}
                                    </router-link>
                                </div>
                            </grid-layout-item>

                            <grid-layout-item class="mt-3 sm:w-1/2 lg:w-1/3">
                                <div class="text-xs tracking-wide uppercase text-grey-darker">Property Type</div>

                                <div class="text-sm capitalize">
                                    {{ listing.property_type }}
                                </div>
                            </grid-layout-item>

                            <grid-layout-item class="mt-3 sm:w-1/2 lg:w-1/6">
                                <div class="text-xs tracking-wide uppercase text-grey-darker">Rent</div>

                                <div class="text-sm">
                                    {{ listing.rent_amount | money(0) }}/mo.
                                </div>
                            </grid-layout-item>

                            <grid-layout-item class="mt-3 sm:w-1/2 lg:w-1/6">
                                <div class="text-xs tracking-wide uppercase text-grey-darker">Deposit</div>

                                <div class="text-sm">
                                    {{ listing.deposit_amount || 0 | money(0) }}.
                                </div>
                            </grid-layout-item>

                            <grid-layout-item class="mt-3 sm:w-1/2 lg:w-1/3">
                                <div class="text-xs tracking-wide uppercase text-grey-darker">Available</div>

                                <div class="text-sm">
                                    {{ listing.available_at | moment('D MMM, YYYY') }}
                                </div>
                            </grid-layout-item>

                            <grid-layout-item class="mt-3 sm:w-1/2 lg:w-1/3">
                                <div class="text-xs tracking-wide uppercase text-grey-darker">Rooms</div>

                                <div class="text-sm">
                                    {{ listing.number_of_bedrooms }} Bed, {{ listing.number_of_bathrooms }} Bath &amp; {{ listing.number_of_receptions }} Reception.
                                </div>
                            </grid-layout-item>

                            <grid-layout-item class="mt-3 sm:w-1/2 lg:w-1/3">
                                <div class="text-xs tracking-wide uppercase text-grey-darker">Features &amp; Allowances</div>

                                <div class="text-sm">
                                    {{
                                        _.chain({
                                            'Furnished': listing.is_furnished,
                                            'Parking': listing.has_parking,
                                            'Garden': listing.has_garden,
                                            'Pets': listing.allows_pets,
                                            'DSS': listing.allows_dss,
                                            'Smoking': listing.allows_smoking
                                        })
                                        .pickBy(value => value === true)
                                        .keys()
                                        .value()
                                        | comma-delimited({
                                            callback: v => v,
                                        })
                                    }}.
                                </div>

                                <pre>{{
                                }}</pre>
                            </grid-layout-item>
                        </grid-layout>
                    </template>
                </details-card>

                <grid-layout>
                    <grid-layout-item class="mb-6 md:w-1/3">
                        <div class="flex flex-col bg-white rounded shadow md:h-full id-listing-status-card">
                            <header class="flex items-center flex-none h-16 px-4 border-b sm:px-6">
                                <h3 class="flex-grow text-base font-normal">Listing Status</h3>

                                <span
                                    :class="listing.is_active ? 'bg-green-lighter text-green-darker' : 'text-grey-darker bg-grey-lighter'"
                                    class="flex-none px-3 py-1 text-sm font-semibold rounded-full"
                                >
                                    {{ listing.is_active ? 'Active' : 'Draft' }}
                                </span>
                            </header>

                            <div class="flex-grow px-4 py-3 text-grey-darker sm:px-6">
                                <template v-if="listing.is_active">
                                    <laroute-link
                                        :to="{ path: listing.links.www.show }"
                                        class="w-full mb-6 btn btn-blue-lightest"
                                    >
                                        View Listing
                                    </laroute-link>

                                    <form-item
                                        sid="_"
                                        name="link"
                                        class="mb-3!"
                                    >
                                        <template slot="label">Link to listing</template>

                                        <input-text
                                            name="link"
                                            :value="listing.links.www.show"
                                        />

                                        <template slot="hint">Share this link with prospective tenants</template>
                                    </form-item>

                                    <div class="flex justify-center">
                                        <a
                                            :href="`https://www.facebook.com/sharer/sharer.php?u=${listing.links.www.show}`"
                                            class="block px-2"
                                            rel="noopener"
                                            target="_blank"
                                        >
                                            <base-svg
                                                name="icon-facebook-circle"
                                                class="w-12 h-12 fill-primary-blue-facebook fill-secondary-white"
                                            />
                                        </a>

                                        <a
                                            :href="`https://twitter.com/intent/tweet?url=${listing.links.www.show}&text=&via=rentorauk`"
                                            class="block px-2"
                                            rel="noopener"
                                            target="_blank"
                                        >
                                            <base-svg
                                                name="icon-twitter-circle"
                                                class="w-12 h-12 fill-primary-blue-twitter fill-secondary-white"
                                            />
                                        </a>

                                        <base-dropdown placement="bottom-start">
                                            <template v-slot:trigger="{ open }">
                                                <button
                                                    class="block px-2 focus:outline-none"
                                                    v-clipboard:copy="listing.links.www.show"
                                                    v-clipboard:success="open"
                                                >
                                                    <base-svg
                                                        name="icon-link-circle"
                                                        class="w-12 h-12 fill-primary-blue fill-secondary-white"
                                                    />
                                                </button>
                                            </template>

                                            <template v-slot:menu>
                                                <div class="max-w-xs px-2 py-1 text-sm dropdown-menu mt-3px">
                                                    Link copied!
                                                </div>
                                            </template>
                                        </base-dropdown>
                                    </div>
                                </template>
                                <template v-else>
                                    <laroute-link
                                        :to="{ path: listing.links.www.show }"
                                        class="w-full mb-6 btn btn-blue-lightest"
                                    >
                                        Preview Listing
                                    </laroute-link>

                                    <p>Active your listing to allow prospective tenants to apply.</p>
                                </template>
                            </div>

                            <listing-activation-store-button
                                v-if="!listing.is_active"
                                :listing="listing"
                                @success="find"
                            >
                                Activate Listing
                            </listing-activation-store-button>

                            <listing-activation-delete-button
                                v-else
                                :listing="listing"
                                @success="find"
                            >
                                Deactivate Listing
                            </listing-activation-delete-button>
                        </div>
                    </grid-layout-item>

                    <grid-layout-item class="mb-6 md:w-2/3">
                        <div class="flex flex-col h-full bg-white rounded shadow id-recent-applications-card">
                            <header class="flex items-center flex-none h-16 px-4 border-b sm:px-6">
                                <h3 class="text-base font-normal">Recent Applications</h3>
                            </header>

                            <div
                                v-if="listing.applications.meta.total == 0"
                                class="flex-grow p-3 italic text-center rounded-b text-grey bg-grey-bg"
                            >
                                There's nothing here!
                            </div>

                            <template v-else>
                                <div class="flex-grow">
                                    <table-ish>
                                        <table-ish-row-router-link
                                            v-for="application in listing.applications.data"
                                            :key="application.uuid"
                                            :to="{
                                                name: $router.fix('dashboard.*.applications.show'),
                                                params: {
                                                    application: application.uuid
                                                }
                                            }"
                                        >
                                            <table-ish-cell>
                                                <truncate-hack>
                                                    {{ application.applicants.data | comma-delimited({
                                                        callback: applicant => {
                                                            if ($gate.role('tenant') && _.get(applicant, 'tenant.uuid') === tenant.uuid) {
                                                                return 'You'
                                                            }

                                                            return applicant.name
                                                        },
                                                        total: application.applicants.meta.total
                                                    }) }}
                                                </truncate-hack>
                                            </table-ish-cell>
                                        </table-ish-row-router-link>
                                    </table-ish>
                                </div>

                                <div
                                    v-if="listing.applications.meta.last_page > listing.applications.meta.page"
                                    class="flex-none px-4 py-2 text-sm font-semibold bg-grey-bg text-grey-darker sm:px-6"
                                >
                                    <base-more
                                        :to="{
                                            name: 'dashboard.landlords.applications.index',
                                            query: {
                                                listing: listing.uuid
                                            }
                                        }"
                                        :count="listing.applications.meta.count"
                                        :total="listing.applications.meta.total"
                                    />
                                </div>
                            </template>
                        </div>
                    </grid-layout-item>
                </grid-layout>

                <notes-card
                    :note="listing.note"
                    :notable="listing"
                    class="mb-6"
                />

                <div class="rounded shadow bg-grey-bg id-description-and-media-card">
                    <header class="flex items-center h-16 px-4 bg-white border-b rounded-t sm:px-6">
                        <h3 class="text-base font-normal">Description & Media</h3>
                    </header>

                    <div class="my-3 ml-3 text-xs tracking-wide uppercase text-grey-darker sm:ml-6">Description</div>

                    <div class="mx-3 text-sm sm:mx-6">
                        <base-expandable>
                            <template v-slot:short>
                                <div class="whitespace-pre-wrap">{{ listing.description | limit(300) }}</div>
                            </template>

                            <template v-slot:long>
                                <div class="whitespace-pre-wrap">{{ listing.description }}</div>
                            </template>

                            <template v-slot:trigger="{ isOpen: is_open, toggle }">
                                <button
                                    class="inline-flex items-center mt-1 text-blue"
                                    @click.prevent="toggle"
                                >
                                    Read {{ is_open ? 'less' : 'more' }}

                                    <base-svg
                                        :name="`icon-cheveron-${is_open ? 'up' : 'down'}`"
                                        :key="`icon-cheveron-${is_open ? 'up' : 'down'}`"
                                        class="w-5 h-5 fill-primary-blue"
                                    />
                                </button>
                            </template>
                        </base-expandable>
                    </div>

                    <div class="mt-6 ml-3 text-xs tracking-wide uppercase text-grey-darker sm:ml-6">Photos</div>

                    <files-list
                        :files="listing.files.data"
                        class="md:-mt-8!"
                    />

                    <div class="mt-6 ml-3 text-xs tracking-wide uppercase text-grey-darker sm:ml-6">Video</div>

                    <div class="pb-3 mx-3 sm:mx-6">
                        <a
                            v-if="listing.video_url"
                            :href="listing.video_url"
                            class="inline-flex items-center text-blue"
                            target="_blank"
                            rel="noopener"
                        >
                            {{ listing.video_url }}

                            <base-svg
                                name="icon-external-window"
                                class="w-4 h-4 ml-1 fill-primary-blue fill-secondary-blue"
                            />
                        </a>

                        <base-na v-else />
                    </div>
                </div>
            </template>
        </div>
    </listings-find>
</template>

<script>
    import scroll from '../../../../support/scroll'
    import DetailsCard from '../../../../components/details-card'
    import EditDropdown from './show/edit-dropdown'
    import FilesList from '../../../../components/files-list'
    import ListingActivationStoreButton from './show/listing-activation-store-button'
    import ListingActivationDeleteButton from './show/listing-activation-delete-button'
    import ListingsFind from '../../../../components/listings-find'
    import NotesCard from '../../../../components/notes-card'

    export default {
        components: {
            DetailsCard,
            EditDropdown,
            FilesList,
            ListingActivationStoreButton,
            ListingActivationDeleteButton,
            ListingsFind,
            NotesCard
        },
        beforeRouteLeave (to, from, next) {
            this.$introjs.exit()

            next()
        },
        created () {
            this.$eventBus.$on('the-layout-dashboard:is_sidebar_open', () => {
                this.$introjs.exit()
            })
        },
        methods: {
            introjs () {
                this.$introjs.start([
                    {
                        intro:
                            '<p class="mb-3">The listing details are shown here.</p>' +
                            '<p>More options are available from the <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="inline-block w-5 h-5 icon-dots-horizontal"> <path fill-rule="evenodd" clip-rule="evenodd" d="M5 14C4.46957 14 3.96086 13.7893 3.58579 13.4142C3.21071 13.0391 3 12.5304 3 12C3 11.4696 3.21071 10.9609 3.58579 10.5858C3.96086 10.2107 4.46957 10 5 10C5.53043 10 6.03914 10.2107 6.41421 10.5858C6.78929 10.9609 7 11.4696 7 12C7 12.5304 6.78929 13.0391 6.41421 13.4142C6.03914 13.7893 5.53043 14 5 14ZM12 14C11.4696 14 10.9609 13.7893 10.5858 13.4142C10.2107 13.0391 10 12.5304 10 12C10 11.4696 10.2107 10.9609 10.5858 10.5858C10.9609 10.2107 11.4696 10 12 10C12.5304 10 13.0391 10.2107 13.4142 10.5858C13.7893 10.9609 14 11.4696 14 12C14 12.5304 13.7893 13.0391 13.4142 13.4142C13.0391 13.7893 12.5304 14 12 14ZM19 14C18.4696 14 17.9609 13.7893 17.5858 13.4142C17.2107 13.0391 17 12.5304 17 12C17 11.4696 17.2107 10.9609 17.5858 10.5858C17.9609 10.2107 18.4696 10 19 10C19.5304 10 20.0391 10.2107 20.4142 10.5858C20.7893 10.9609 21 11.4696 21 12C21 12.5304 20.7893 13.0391 20.4142 13.4142C20.0391 13.7893 19.5304 14 19 14Z" class="primary"></path></svg> menu, for example, editing the listing.</p>',
                        element: '.id-details-card',
                        position: 'bottom'
                    },
                    {
                        intro:
                            '<p class="mb-3">You can activate/deactivate the listing here - toggling whether the listing is visible to the public or not.</p>' +
                            '<p>Listings are initially inactive so you can preview and tweak it before going live.</p>',
                        element: '.id-listing-status-card'
                    },
                    {
                        intro: '<p>Recent applications for this listing are listed here.</p>',
                        element: '.id-recent-applications-card'
                    },
                    {
                        intro: '<p class="mb-3">Really sell your properties. Create beautiful listings with well written descriptions, plenty of appealing photos and if possible, a video tour.</p>',
                        element: '.id-description-and-media-card'
                    }
                ])
                    .oncomplete(() => {
                        scroll.toTop()
                    })
            },
            getYouTubeId (url) {
                var regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
                var match = url.match(regExp)

                if (match && match[2].length === 11) {
                    return match[2]
                }
            }
        }
    }
</script>
